import React from "react";

export interface PriceFilter {
  priceRange?: {
    min?: number;
    max?: number;
  };
  priceSort?: "asc" | "desc";
}

const formatPrice = (price: number): string => {
  if (price >= 1000000) {
    return `${(price / 1000000).toFixed(1)}M`;
  }
  if (price >= 1000) {
    return `${Math.round(price / 1000)}k`;
  }
  return price.toString();
};

export const formatPriceFilterLabel = ({
  priceRange,
  priceSort,
}: PriceFilter): React.ReactNode => {
  const label = React.createElement(
    "div",
    { className: "flex items-center gap-2" },
    [
      !priceRange?.min && !priceRange?.max && !priceSort
        ? "Price"
        : [
            priceRange?.min && priceRange?.max
              ? `$${formatPrice(priceRange.min)} - $${formatPrice(
                  priceRange.max
                )}`
              : priceRange?.min
              ? `$${formatPrice(priceRange.min)}+`
              : priceRange?.max
              ? `Up to $${formatPrice(priceRange.max)}`
              : "Price",
            priceSort &&
              React.createElement("svg", {
                key: "sort-icon",
                className: "w-3.5 h-3.5",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: [
                  React.createElement("path", {
                    key: "arrow",
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d:
                      priceSort === "desc"
                        ? "M19 14l-7 7m0 0l-7-7m7 7V3" // Down arrow
                        : "M5 10l7-7m0 0l7 7m-7-7v18", // Up arrow
                  }),
                ],
              }),
          ],
    ]
  );

  return label;
};

export interface BedsAndBathsFilter {
  bedrooms?: string;
  bathrooms?: string;
  useExactMatchBeds?: boolean;
  useExactMatchBaths?: boolean;
}

export const formatBedsAndBathsLabel = ({
  bedrooms,
  bathrooms,
  useExactMatchBeds,
  useExactMatchBaths,
}: BedsAndBathsFilter): string => {
  if (!bedrooms && !bathrooms) {
    return "Beds & Baths";
  }

  const parts = [];

  if (bedrooms) {
    const number = bedrooms.replace("+", "");
    parts.push(
      `${number}${!useExactMatchBeds && bedrooms.includes("+") ? "+" : ""} bed`
    );
  }

  if (bathrooms) {
    const number = bathrooms.replace("+", "");
    parts.push(
      `${number}${
        !useExactMatchBaths && bathrooms.includes("+") ? "+" : ""
      } bath`
    );
  }

  return parts.join(", ");
};

// Convert FilterState to API filter format
export const convertToApiFilters = (filters: any) => {
  const apiFilters: Record<string, any> = {};

  // If MLS Numbers (listingid) are provided, only use that filter and ignore all others
  if (filters.mlsNumbers && filters.mlsNumbers.length > 0) {
    apiFilters.listingid = filters.mlsNumbers;
    return apiFilters;
  }

  // Status filter - capitalize first letter and add to standard_status
  if (filters.status) {
    if (Array.isArray(filters.status)) {
      // If status is an array, keep it as an array but capitalize each status
      const capitalizedStatuses = filters.status.map(
        (status: string) =>
          status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
      );

      // If "Active" is selected, automatically include "Active Under Contract"
      if (capitalizedStatuses.includes("Active")) {
        const statusSet = new Set(capitalizedStatuses);
        statusSet.add("Active Under Contract");
        apiFilters.standard_status = Array.from(statusSet);
      } else {
        apiFilters.standard_status = capitalizedStatuses;
      }
    } else {
      // If status is a string, capitalize first letter
      const capitalizedStatus =
        filters.status.charAt(0).toUpperCase() +
        filters.status.slice(1).toLowerCase();

      // If "Active" is selected, automatically include "Active Under Contract"
      if (capitalizedStatus === "Active") {
        apiFilters.standard_status = ["Active", "Active Under Contract"];
      } else {
        apiFilters.standard_status = capitalizedStatus;
      }
    }
  }

  // Price filters
  if (filters.priceRange) {
    if (filters.priceRange.min) {
      apiFilters.min_price = filters.priceRange.min;
    }
    if (filters.priceRange.max) {
      apiFilters.max_price = filters.priceRange.max;
    }
  }

  // Beds and baths
  if (filters.bedrooms) {
    const beds = parseInt(filters.bedrooms.replace("+", ""));
    apiFilters.bedrooms = beds;
  }
  if (filters.bathrooms) {
    const baths = parseInt(filters.bathrooms.replace("+", ""));
    apiFilters.bathrooms = baths;
  }

  // Square footage
  if (filters.sqftRange) {
    if (filters.sqftRange.min) {
      apiFilters.sqft_min = filters.sqftRange.min;
    }
    if (filters.sqftRange.max) {
      apiFilters.sqft_max = filters.sqftRange.max;
    }
  }

  // Boolean features
  if (filters.features) {
    if (filters.features.waterfront) apiFilters.waterfront = true;
    if (filters.features.pool) apiFilters.pool = true;
    if (filters.features.basement) apiFilters.basement = true;
    if (filters.features.garage) apiFilters.garage = true;
    if (filters.features.cityView) apiFilters.city_view = true;
    if (filters.features.mountainView) apiFilters.mountain_view = true;
    if (filters.features.parkView) apiFilters.park_view = true;
    if (filters.features.waterView) apiFilters.water_view = true;
  }

  // New construction and rental property filters
  if (filters.newconstructionyn) apiFilters.newconstructionyn = true;
  if (filters.rentalyn) apiFilters.rentalyn = true;

  // Property type
  if (filters.propertyType) {
    apiFilters.property_sub_type = filters.propertyType;
  }

  // Subdivision
  if (filters.subdivision) {
    apiFilters.subdivision = filters.subdivision;
  }

  // ZIP Code
  if (filters.zipCode) {
    apiFilters.zip_code = filters.zipCode;
  }

  // County — single name, case-insensitive on the backend
  if (filters.county) {
    apiFilters.county = filters.county;
  }

  // City — refines/overrides the default location city
  if (filters.city) {
    apiFilters.city = filters.city;
  }

  // Keywords (publicremarks)
  if (filters.publicremarks) {
    apiFilters.publicremarks = filters.publicremarks;
  }

  return apiFilters;
};
